import { ArticleContentType } from '@features/article/types';
import type { MouseEventHandler } from 'react';
import { ContextPageContentType } from 'src/typings/types';

export function getSnapshotFilename(value: string) {
  return `./__snapshots__/${value}.output.html`;
}

// Function expects any parameter
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounceCallback<T extends (...args: any[]) => void>(func: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function getHeaderElement() {
  return document.getElementsByTagName('header')?.[0];
}

function getHeaderOffset() {
  const headerElement = getHeaderElement();

  if (!headerElement) {
    return 0;
  }

  return headerElement.offsetHeight + 10;
}

export function scrollToTarget(id: string, offset?: number): MouseEventHandler<HTMLAnchorElement> {
  return function (event) {
    event.preventDefault();
    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    const elementOffset = element.offsetTop;
    window.scrollTo(0, elementOffset - (offset || getHeaderOffset()));
  };
}

export function scrollTargetIntoView(id: string): MouseEventHandler<HTMLAnchorElement> {
  return function (event) {
    event.preventDefault();
    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth' });
  };
}

export function formatContentType(contentType?: ArticleContentType): ContextPageContentType {
  return (
    contentType ? contentType.toLowerCase().replace(/\s+/g, '_') : 'other'
  ) as ContextPageContentType;
}

export function parseCurrencyString(currencyString: string, locale = 'nl-NL') {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR'
    });
    const parts = formatter.formatToParts(0);
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value || ','; // Default to comma
    const groupSeparator = parts.find(part => part.type === 'group')?.value || '.'; // Default to period

    const cleanedString = currencyString
      .replace(new RegExp(`[^\\d${decimalSeparator}${groupSeparator}]`, 'g'), '')
      .replace(new RegExp(`\\${groupSeparator}`, 'g'), '')
      .replace(decimalSeparator, '.');

    return parseFloat(cleanedString);
  } catch (error) {
    return NaN;
  }
}

export function capitalizeFirstLetter(value: string): string {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}
